/** HELPHOVER1: help text is a hover tooltip on the element it explains, never a separate info icon (design/style-guide.md, section 11). */
let tipElement: HTMLElement | undefined;
let tipAnchor: HTMLElement | undefined;

const FOCUSABLE = "a[href], button, input, select, textarea, summary, [tabindex]";

export function hideHoverHelp(): void {
  tipElement?.remove();
  tipElement = undefined;
  tipAnchor = undefined;
  document.removeEventListener("keydown", onEscape);
  document.removeEventListener("pointerdown", hideHoverHelp, true);
}

function onEscape(event: KeyboardEvent): void {
  if (event.key === "Escape") hideHoverHelp();
}

function show(anchor: HTMLElement, text: string): void {
  if (tipAnchor === anchor && tipElement?.isConnected === true) return;
  hideHoverHelp();
  const tip = document.createElement("div");
  tip.className = "hover-tip";
  tip.setAttribute("role", "tooltip");
  tip.dataset["testid"] = "hover-tip";
  tip.textContent = text;
  document.body.append(tip);
  const margin = 8;
  const gap = 6;
  const box = anchor.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const width = Math.min(320, viewportWidth - margin * 2);
  tip.style.width = `${width}px`;
  const height = tip.offsetHeight;
  const left = Math.max(margin, Math.min(box.left, viewportWidth - width - margin));
  const below = box.bottom + gap;
  const top = below + height + margin <= viewportHeight ? below : Math.max(margin, box.top - gap - height);
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
  tipElement = tip;
  tipAnchor = anchor;
  document.addEventListener("keydown", onEscape);
  document.addEventListener("pointerdown", hideHoverHelp, true);
}

/** Shows `text` on hover or keyboard focus of `target`. `describe` is the element that carries the aria-description (default: the target). */
export function bindHoverTip(target: HTMLElement, text: string, describe: HTMLElement = target): HTMLElement {
  if (text === "") return target;
  target.classList.add("has-hover-help");
  describe.setAttribute("aria-description", text);
  // A heading or tile that holds no control of its own becomes a tab stop, so a keyboard user reaches the same help.
  if (!target.matches(FOCUSABLE) && target.querySelector(FOCUSABLE) === null) target.tabIndex = 0;
  target.addEventListener("mouseenter", () => show(target, text));
  target.addEventListener("mouseleave", () => {
    if (tipAnchor === target) hideHoverHelp();
  });
  target.addEventListener("focusin", () => show(target, text));
  target.addEventListener("focusout", () => {
    if (tipAnchor === target) hideHoverHelp();
  });
  return target;
}

/** The shared help helper: the same what / where / example text the circled "?" used to hold, now on `target` itself. */
export function hoverHelp(target: HTMLElement, what: string, where = "", example = ""): HTMLElement {
  const text = [what, where, example === "" ? "" : `Example: ${example}`].filter((part) => part !== "").join(" ");
  return bindHoverTip(target, text);
}

const liveHelp = new WeakMap<HTMLElement, { text: string }>();

/** HIGFIX4A: hover help for an element that stays on the page while its text changes (a page title that
 * serves many views). Listeners are bound once; an empty `text` clears the help and the tab stop. */
export function setHoverHelp(target: HTMLElement, text: string): void {
  let live = liveHelp.get(target);
  if (live === undefined) {
    const cell = { text: "" };
    live = cell;
    liveHelp.set(target, cell);
    const open = (): void => {
      if (cell.text !== "") show(target, cell.text);
    };
    target.addEventListener("mouseenter", open);
    target.addEventListener("focusin", open);
    const close = (): void => {
      if (tipAnchor === target) hideHoverHelp();
    };
    target.addEventListener("mouseleave", close);
    target.addEventListener("focusout", close);
  }
  live.text = text;
  target.classList.toggle("has-hover-help", text !== "");
  if (text === "") {
    target.removeAttribute("aria-description");
    target.removeAttribute("tabindex");
    if (tipAnchor === target) hideHoverHelp();
    return;
  }
  target.setAttribute("aria-description", text);
  if (!target.matches("a[href], button, input, select, textarea, summary")) target.tabIndex = 0;
}

/** HIGFIX4A (design/style-guide.md section 11): a help paragraph that sat on the page moves into hover help on the element it
 * explains. The paragraph stays in the document, visually hidden, so the control it described keeps its aria-describedby text
 * and a script that rewrites the paragraph (a bound filled in from settingsBounds) is still what the tip shows. Several
 * paragraphs for one anchor are joined. A label anchor also shows the tip while its control has keyboard focus, so a label is
 * never a second tab stop. */
export function moveHelpToHover(anchor: HTMLElement, sources: readonly HTMLElement[]): void {
  for (const source of sources) source.classList.add("help-in-hover");
  const text = (): string =>
    sources
      .map((source) => (source.textContent ?? "").trim())
      .filter((part) => part !== "")
      .join(" ");
  anchor.classList.add("has-hover-help");
  const control = anchor instanceof HTMLLabelElement ? anchor.control : null;
  const open = (): void => {
    const now = text();
    if (now !== "") show(anchor, now);
  };
  const close = (): void => {
    if (tipAnchor === anchor) hideHoverHelp();
  };
  anchor.addEventListener("mouseenter", open);
  anchor.addEventListener("mouseleave", close);
  if (control !== null) {
    control.addEventListener("focusin", open);
    control.addEventListener("focusout", close);
  } else {
    if (!anchor.matches(FOCUSABLE) && anchor.querySelector(FOCUSABLE) === null) anchor.tabIndex = 0;
    anchor.addEventListener("focusin", open);
    anchor.addEventListener("focusout", close);
  }
}

/** Wires every `[data-help-hover-on="<anchor id>"]` paragraph in `root`: the paragraph moves into hover help on that anchor. */
export function wireHelpInHover(root: ParentNode = document): void {
  const byAnchor = new Map<string, HTMLElement[]>();
  for (const source of root.querySelectorAll<HTMLElement>("[data-help-hover-on]")) {
    const id = source.dataset["helpHoverOn"] ?? "";
    byAnchor.set(id, [...(byAnchor.get(id) ?? []), source]);
  }
  for (const [id, sources] of byAnchor) {
    const anchor = document.getElementById(id);
    if (anchor !== null) moveHelpToHover(anchor, sources);
  }
}
